// ============================================================================
// VINStep.jsx - Enhanced with VIN Barcode Scanning & Mismatch Handling
// ============================================================================
// Features:
// - Manual VIN entry (desktop/mobile)
// - Camera barcode scanning (mobile only)
// - VIN mismatch detection with options to reject or update
// - Supports Code 39 and Code 128 (standard VIN barcode formats)
// ============================================================================

import React, { Component } from 'react';
import { HTTPService } from "./../../../services/httpService";
import VINScanner from './VINScanner';

const API = new HTTPService();

class VINStep extends Component {
  constructor(props) {
    super(props);

    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Check if VIN is system-generated (starts with "NV" or "OT")
    const isSystemVIN = props.vin && (
      props.vin.toUpperCase().startsWith('NV') ||
      props.vin.toUpperCase().startsWith('OT')
    );

    this.state = {
      vin: isSystemVIN ? '' : (props.vin || ''), // Don't show system-generated VINs
      vinConfirmed: false,
      loading: false,
      error: null,

      // Scanner state
      isMobile: isMobile,
      showScanner: false,
      scannerAvailable: false,

      // Mismatch state
      showMismatch: false,
      mismatchData: null  // { expected: {...}, decoded: {...}, comparison: {...} }
    };

    this.handleVINChange = this.handleVINChange.bind(this);
    this.handleVerifyVIN = this.handleVerifyVIN.bind(this);
    this.handleVINScanned = this.handleVINScanned.bind(this);
  }

  componentDidMount() {
    // Only check camera availability on mobile devices
    if (this.state.isMobile) {
      this.checkScannerAvailability();
    }
  }

  checkScannerAvailability = async () => {
    try {
      // Check if getUserMedia is supported
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Try to get camera permissions (doesn't actually start camera)
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop()); // Immediately stop

        this.setState({ scannerAvailable: true });
      }
    } catch (error) {
      console.log('Camera not available:', error);
      this.setState({ scannerAvailable: false });
    }
  };

  handleVINChange(event) {
    const vin = event.target.value.toUpperCase();
    this.setState({
      vin: vin,
      error: null,
      showMismatch: false,
      mismatchData: null
    });
  }

  async handleVINScanned(scannedVIN) {
    console.log("📸 VIN scanned:", scannedVIN);

    this.setState({
      vin: scannedVIN.toUpperCase(),
      showScanner: false,
      error: null,
      showMismatch: false,
      mismatchData: null,
      loading: true
    });

    // Automatically verify the scanned VIN
    await this.verifyVIN(scannedVIN.toUpperCase(), false);
  }

  openScanner = () => {
    this.setState({ showScanner: true });
  };

  closeScanner = () => {
    this.setState({ showScanner: false });
  };

  async handleVerifyVIN() {
    const { vin } = this.state;

    // Validate VIN length
    if (vin.length !== 17) {
      this.setState({ error: 'VIN must be exactly 17 characters' });
      return;
    }

    // Basic VIN validation (alphanumeric, no I, O, Q)
    const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
    if (!vinPattern.test(vin)) {
      this.setState({ error: 'Invalid VIN format. VIN cannot contain I, O, or Q.' });
      return;
    }

    await this.verifyVIN(vin, false);
  }

  async verifyVIN(vin, forceUpdate = false, vinOnly = false, decodedRef = null) {
    try {
      this.setState({ loading: true, error: null });

      console.log("🔍 Verifying VIN:", vin, forceUpdate ? "(force update)" : "", vinOnly ? "(vin only)" : "");

      const payload = {
        vin: vin,
        vin_confirmed: true,
        force_update: forceUpdate,
        vin_only: vinOnly
      };

      if (forceUpdate && decodedRef) {
        payload.decoded_ref = {
          make: decodedRef.make,
          model: decodedRef.model,
          trim: decodedRef.trim,
          id_car_make: decodedRef.id_car_make,
          id_car_model: decodedRef.id_car_model,
          id_car_trim: decodedRef.id_car_trim
        };
      }

      // Call backend to verify VIN
      const { data } = await API.post(
        `/requests/${this.props.requestId}/vin/verify`,
        payload
      );

      // Check for mismatch response
      if (!data.success && data.mismatch) {
        console.log("⚠️ VIN mismatch detected:", data);
        this.setState({
          loading: false,
          showMismatch: true,
          mismatchData: {
            expected: data.expected,
            decoded: data.decoded,
            comparison: data.comparison
          }
        });
        return;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to verify VIN');
      }

      console.log("✅ VIN verified successfully", data.wasUpdated ? "(vehicle updated)" : "");

      this.setState({
        vinConfirmed: true,
        loading: false,
        showMismatch: false,
        mismatchData: null
      });

      // Notify parent component
      this.props.onVINVerified(vin);

    } catch (error) {
      console.error("❌ Error verifying VIN:", error);
      this.setState({
        loading: false,
        error: error.message
      });
    }
  }

  // Handle "Update Request" - accept mismatched VIN and update vehicle info
  handleUpdateRequest = async () => {
    const { vin, mismatchData } = this.state;
    console.log("📝 User chose to update request with new VIN");
    await this.verifyVIN(vin, true, false, mismatchData.decoded);
  };

  // Handle "Keep Original" - accept the VIN but keep original vehicle details
  handleKeepOriginal = async () => {
    const { vin } = this.state;
    console.log("📝 User chose to keep original vehicle, VIN only update");
    await this.verifyVIN(vin, false, true); // Re-submit with vin_only: true
  };

  // Handle "Go Back" - return to VIN entry
  handleCancelMismatch = () => {
    this.setState({
      showMismatch: false,
      mismatchData: null,
      vin: '',
      error: null
    });
  };

  // Render mismatch UI
  renderMismatchUI() {
    const { mismatchData } = this.state;
    if (!mismatchData) return null;

    const { expected, decoded, comparison } = mismatchData;

    return (
      <div className="vin-mismatch">
        <div className="alert alert-warning mb-4">
          <h5 className="alert-heading">
            <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
            Vehicle Mismatch Detected
          </h5>
          <p className="mb-0">
            The VIN entered does not match the expected vehicle for this job.
          </p>
        </div>

        <div className="row mb-4">
          {/* Expected Vehicle */}
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="card border-secondary">
              <div className="card-header bg-secondary text-white">
                <i className="fa-sharp fa-regular fa-file-alt mr-2"></i>
                Expected (From Request)
              </div>
              <div className="card-body">
                <h5 className="card-title">
                  {expected.year} {expected.make} {expected.model}
                </h5>
                {expected.trim && (
                  <p className="card-text text-muted mb-2">{expected.trim}</p>
                )}
                <p className="card-text">
                  <small className="text-muted">
                    VIN: {expected.isSystemVin ? <em>Not provided</em> : expected.vin}
                  </small>
                </p>
              </div>
            </div>
          </div>

          {/* Decoded Vehicle */}
          <div className="col-md-6">
            <div className="card border-primary">
              <div className="card-header bg-primary text-white">
                <i className="fa-sharp fa-regular fa-car mr-2"></i>
                Actual (From VIN)
              </div>
              <div className="card-body">
                <h5 className="card-title">
                  {decoded.year} {decoded.make} {decoded.model}
                </h5>
                {decoded.trim && (
                  <p className="card-text text-muted mb-2">{decoded.trim}</p>
                )}
                <p className="card-text">
                  <small className="text-muted">VIN: {decoded.vin}</small>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Details */}
        <div className="card bg-light mb-4">
          <div className="card-body">
            <h6 className="card-title">Differences:</h6>
            <ul className="mb-0">
              {!comparison.yearMatch && (
                <li>
                  <strong>Year:</strong> Expected {expected.year}, Actual {decoded.year}
                </li>
              )}
              {!comparison.makeMatch && (
                <li>
                  <strong>Make:</strong> Expected {expected.make}, Actual {decoded.make}
                </li>
              )}
              {!comparison.modelMatch && (
                <li>
                  <strong>Model:</strong> Expected {expected.model}, Actual {decoded.model}
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mismatch-actions">
          <p className="font-weight-bold mb-3">What would you like to do?</p>

          <div className="row justify-content-center">
            <div className="col-md-4 mb-3">
              <button
                className="btn btn-success btn-block btn-lg"
                onClick={this.handleUpdateRequest}
                disabled={this.state.loading}
              >
                {this.state.loading ? (
                  <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                ) : (
                  <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
                )}
                UPDATE VEHICLE
                <small className="d-block mt-1">Use decoded vehicle info</small>
              </button>
            </div>

            <div className="col-md-4 mb-3">
              <button
                className="btn btn-primary btn-block btn-lg"
                onClick={this.handleKeepOriginal}
                disabled={this.state.loading}
              >
                {this.state.loading ? (
                  <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                ) : (
                  <i className="fa-sharp fa-regular fa-car mr-2"></i>
                )}
                KEEP ORIGINAL
                <small className="d-block mt-1">Accept VIN, keep vehicle as-is</small>
              </button>
            </div>

            <div className="col-md-4 mb-3">
              <button
                className="btn btn-secondary btn-block btn-lg"
                onClick={this.handleCancelMismatch}
              >
                <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>
                GO BACK
                <small className="d-block mt-1">Re-enter VIN</small>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    // Show scanner fullscreen
    if (this.state.showScanner) {
      return (
        <VINScanner
          onVINScanned={this.handleVINScanned}
          onClose={this.closeScanner}
        />
      );
    }

    // Show mismatch UI
    if (this.state.showMismatch) {
      return (
        <div className="vin-step">
          {!this.props.hideStepHeader && <h4 className="text-center mb-4">Step 1: Verify VIN</h4>}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {this.renderMismatchUI()}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="vin-step">
        <h4 className="text-center mb-4">Step 1: Verify VIN</h4>

        <div className="row justify-content-center">
          <div className="col-md-8">

            {/* Scanner Button (Mobile Only) */}
            {this.state.isMobile && this.state.scannerAvailable && !this.state.vinConfirmed && (
              <div className="text-center mb-3">
                <button
                  className="btn btn-outline-primary btn-lg btn-block"
                  onClick={this.openScanner}
                  disabled={this.state.loading}
                >
                  <i className="fa-sharp fa-regular fa-barcode mr-2"></i>
                  Scan VIN Barcode
                </button>
              </div>
            )}

            {/* Divider */}
            {this.state.isMobile && this.state.scannerAvailable && !this.state.vinConfirmed && (
              <div className="text-center my-3">
                <small className="text-muted">OR</small>
              </div>
            )}

            {/* VIN Input */}
            <div className="form-group">
              <label htmlFor="vin-input" className="font-weight-bold">
                17-Character Vehicle ID
              </label>
              <input
                id="vin-input"
                type="text"
                className="form-control form-control-lg text-center"
                value={this.state.vin}
                onChange={this.handleVINChange}
                placeholder="Enter vehicle identification number"
                maxLength={17}
                disabled={this.state.loading || this.state.vinConfirmed}
                style={{
                  letterSpacing: '2px',
                  fontFamily: 'monospace',
                  fontSize: '1.2rem'
                }}
              />
            </div>

            {/* Error Display */}
            {this.state.error && (
              <div className="alert alert-danger" role="alert">
                <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
                {this.state.error}
              </div>
            )}

            {/* Success Display */}
            {this.state.vinConfirmed && (
              <div className="alert alert-success" role="alert">
                <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
                Verified successfully! Click NEXT to continue.
              </div>
            )}

            {/* Verify Button */}
            {!this.state.vinConfirmed && (
              <div className="text-center mt-4">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={this.handleVerifyVIN}
                  disabled={this.state.vin.length !== 17 || this.state.loading}
                >
                  {this.state.loading && (
                    <span className="mr-2">
                      <i className="fa-sharp fa-regular fa-refresh fa-spin" />
                    </span>
                  )}
                  VERIFY & CONTINUE
                </button>
              </div>
            )}

            {/* Help Text */}
            <div className="card bg-light mt-4">
              <div className="card-body">
                <h6 className="card-title">
                  <i className="fa-sharp fa-regular fa-info-circle text-primary mr-2"></i>
                  Where to find the VIN:
                </h6>
                <ul className="mb-0 small">
                  <li><strong>Door jamb sticker</strong> (driver's side — best for barcode scanning)</li>
                  <li>Driver's side dashboard (visible through windshield)</li>
                  <li>Vehicle registration or insurance documents</li>
                  <li>Engine block (stamped)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default VINStep;
